import { ReceiptData } from "./receiptTemplate";
import { formatCurrency } from "../config/currency";

// Builds the same receipt content as receiptTemplate.ts's buildReceiptHtml,
// but as raw ESC/POS command bytes for printers that don't have (or can't
// have) a CUPS driver translating a browser's rendered PDF into something
// the printer's firmware understands — see receiptPrinter.ts for why this
// exists alongside the HTML/window.print() path rather than replacing it.

const ESC = 0x1b;
const GS = 0x1d;

// Most 80mm thermal receipt printers print 42 characters per line at the
// default font. Printers configured for a narrower roll (e.g. 58mm) would
// need a smaller value here — no way to detect this from the USB device
// itself, so it's a constant rather than something auto-detected.
const CHARS_PER_LINE = 42;

// None of these truncate when the input is already >= len — column widths
// below are sized for the common case, but a value that doesn't fit (e.g. a
// grand total wide enough to blow past the 9-char PRICE/TOTAL column, like
// "PKR 12,345.00") must never lose digits silently. Overflowing the
// intended column width just pushes the rest of that line over — a purely
// cosmetic misalignment on the printout — which is far preferable to a
// wrong total with no indication anything was cut.
const padRight = (s: string, len: number): string => (s.length >= len ? s : s + " ".repeat(len - s.length));
const padLeft = (s: string, len: number): string => (s.length >= len ? s : " ".repeat(len - s.length) + s);
const center = (s: string, len: number): string => {
  if (s.length >= len) return s;
  const left = Math.floor((len - s.length) / 2);
  return " ".repeat(left) + s + " ".repeat(len - s.length - left);
};
const divider = () => "-".repeat(CHARS_PER_LINE) + "\n";

// Item/Qty/Price/Total column widths, summing to CHARS_PER_LINE (20+4+9+9=42).
const COL_NAME = 20;
const COL_QTY = 4;
const COL_PRICE = 9;
const COL_TOTAL = 9;

const itemLine = (name: string, qty: string, price: string, total: string): string =>
  padRight(name, COL_NAME) + padLeft(qty, COL_QTY) + padLeft(price, COL_PRICE) + padLeft(total, COL_TOTAL) + "\n";

const metaLine = (label: string, value: string): string => {
  const valueWidth = CHARS_PER_LINE - label.length;
  return label + padLeft(value, Math.max(valueWidth, 0)) + "\n";
};

export const buildReceiptEscPos = (s: ReceiptData): Uint8Array => {
  const chunks: number[] = [];
  const text = (str: string) => chunks.push(...Array.from(new TextEncoder().encode(str)));
  const cmd = (...bytes: number[]) => chunks.push(...bytes);
  const storeTitle = (s.storeName || "").trim() || "1MARKAZ";

  cmd(ESC, 0x40); // ESC @ — initialize
  cmd(ESC, 0x47, 0x01); // ESC G 1 — double-strike mode for crisp, darker thermal print density

  // Header — centered, bold, double-size
  cmd(ESC, 0x61, 0x01); // ESC a 1 — center align
  cmd(GS, 0x21, 0x11); // GS ! 0x11 — double width + double height
  cmd(ESC, 0x45, 0x01); // ESC E 1 — bold on
  text(`${storeTitle}\n`);
  cmd(GS, 0x21, 0x00); // back to normal size
  text(s.isOfflineSale ? "Point of Sale (OFFLINE SALE)\n" : "Point of Sale Receipt\n");
  cmd(ESC, 0x61, 0x00); // ESC a 0 — left align

  text(divider());
  // Keep emphasis on so all metadata lines are dark and legible
  const dateStr = s.timestamp.toLocaleDateString("en-PK", { year: "numeric", month: "short", day: "numeric" });
  const timeStr = s.timestamp.toLocaleTimeString("en-PK", { hour: "2-digit", minute: "2-digit" });
  text(metaLine("Order #", s.isOfflineSale ? `${s.orderId} (Offline Ref)` : String(s.orderId)));
  text(metaLine("Date", dateStr));
  text(metaLine("Time", timeStr));
  text(metaLine("Cashier", s.cashier));
  text(metaLine("Type", s.orderType.charAt(0).toUpperCase() + s.orderType.slice(1)));
  if (s.customerName) text(metaLine("Customer", s.customerName));
  if (s.address) text(metaLine("Address", s.address));

  text(divider());
  text(itemLine("Item", "Qty", "Price", "Total"));
  text(divider());

  // Print item rows with bold emphasis so they match the darkness and clarity of bold characters
  for (const item of s.items) {
    const name = item.product_name.length > COL_NAME ? item.product_name.slice(0, COL_NAME - 1) + "…" : item.product_name;
    const lineTotal = (item.unit_price * item.quantity).toFixed(2);
    text(itemLine(name, String(item.quantity), item.unit_price.toFixed(2), lineTotal));
  }

  text(divider());
  text(itemLine("Subtotal", "", "", formatCurrency(s.subtotal)));
  if ((s.catalogDiscountAmount ?? 0) > 0) {
    const label = s.catalogDiscountName ? `Item Discount (${s.catalogDiscountName})` : "Item Discount";
    text(itemLine(label, "", "", `-${(s.catalogDiscountAmount ?? 0).toFixed(2)}`));
  }
  if (s.discountAmount > 0) {
    const label = s.couponName ? `Discount (${s.couponName})` : "Discount";
    text(itemLine(label, "", "", `-${s.discountAmount.toFixed(2)}`));
  }
  cmd(GS, 0x21, 0x01); // double height only, for emphasis without breaking column widths
  text(itemLine("TOTAL", "", "", formatCurrency(s.total)));
  cmd(GS, 0x21, 0x00);

  text(divider());
  text(metaLine("Payment", s.paymentMethod.toUpperCase()));
  if (s.paymentMethod === "cash") {
    text(metaLine("Amount Tendered", formatCurrency(s.amountTendered ?? 0)));
    text(metaLine("Change Due", formatCurrency(s.changeDue ?? 0)));
  }

  text(divider());
  cmd(ESC, 0x61, 0x01);
  text(center("Thank you for shopping!", CHARS_PER_LINE) + "\n");
  text(center("Please come again.", CHARS_PER_LINE) + "\n");
  text(divider());
  text(center("(c) 1Markaz", CHARS_PER_LINE) + "\n");
  cmd(ESC, 0x45, 0x00); // bold off
  cmd(ESC, 0x47, 0x00); // double-strike off
  cmd(ESC, 0x61, 0x00);

  text("\n");
  // GS V 66 4 — "Function B" full cut: feeds 4 lines to the cutting
  // position itself, then cuts, in one command. The simpler 1-byte form
  // (GS V 0) is the more common textbook form and is what this used to
  // send, but some non-Epson clone firmware only implements Function B —
  // sending our own manual feed *and* the simple form's implicit feed
  // compounded into far more blank paper than intended, and the cut
  // itself never fired on that hardware. Function B owns the exact feed
  // distance itself, so there's no double-feeding here.
  cmd(GS, 0x56, 0x42, 0x04);

  return new Uint8Array(chunks);
};
